import { AngularFireAuth } from "@angular/fire/compat/auth";
import { filter, firstValueFrom, of, timeout } from "rxjs";

/**
 * How long to wait for Firebase to read a saved session back from IndexedDB.
 * Right after a page load, currentUser is null for a moment even for someone
 * who is signed in, and callers treat a null token as "signed out" — the 401
 * handler then wiped storage and signed the user out for real.
 */
const SESSION_RESTORE_WAIT_MS = 3000;

// Function for request firebase token
export async function requestToken(
  afAuth: AngularFireAuth
): Promise<string | null> {
  const currentUser = (await afAuth.currentUser) ?? await firstValueFrom(
    afAuth.authState.pipe(
      filter((user) => !!user),
      timeout({ first: SESSION_RESTORE_WAIT_MS, with: () => of(null) }),
    ),
  );
  // console.log(currentUser); //__CONSOLE_LOG__
  // let encryptedData = localStorage.getItem('user');
  // let decryptedData = this.encryptionService.decryptData(this.encryptedData);
  // const user = JSON.parse(this.decryptedData || "{}");
  if (currentUser) {
    try {
      return await currentUser.getIdToken(true);
    } catch (e) {
      // Token refresh failed (e.g. mobile Safari background restriction, network loss)
      // Fall back to cached token rather than crashing
      try {
        return await currentUser.getIdToken(false);
      } catch (e2) {
        return null;
      }
    }
  }

  return null;
}

/**
 * True only for an iOS "Add to Home Screen" PWA — `navigator.standalone` is a
 * Safari-only flag that doesn't exist off iOS, so this never false-positives
 * on Android or desktop.
 *
 * Firebase's popup-based Google/Apple sign-in depends on the popup and its
 * opener sharing sessionStorage to hand back the auth result. iOS's WKWebView
 * partitions storage differently for a standalone PWA than for Safari itself,
 * which breaks that handoff — surfacing as Firebase's own
 * "Unable to process request due to missing initial state" page inside the
 * popup, most reliably when the flow takes an extra hop (e.g. switching
 * Google accounts). Callers use this to head that off with an actionable
 * message instead of leaving the user stuck in a popup that can't complete.
 */
export function isIOSStandalone(): boolean {
  return 'standalone' in window.navigator && (window.navigator as any).standalone === true;
}
